import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import {
  BUILDING_FUND_DONATION_ROUTE_SLUG,
  FAITH_SANCTUARY_CAMPAIGN_SLUG,
  buildPublicOrgPath,
  resolveCanonicalPublicRoute,
} from "../../utils/canonicalPublicRoutes";

import {readPublicOrganizationWith} from "../publicOrganizationReader";

const GENERAL_GIVING_ROUTE = {
  id: "general",
  slug: "general",
  donationRouteId: null,
  transactionPurpose: "giving",
  category: "general",
  givingCategory: "general",
  platformCategory: "general",
  label: "General Giving",
  bucketLabel: "General Giving",
  isFallback: true,
};

function clean(value) {
  if (value == null) return "";
  try {
    return decodeURIComponent(String(value)).trim();
  } catch {
    return String(value).trim();
  }
}

function cleanSegment(value) {
  return clean(value).replace(/^\/+|\/+$/g, "");
}

function encodeSegment(value) {
  return encodeURIComponent(cleanSegment(value));
}

function normalizeCategory(value, fallback = "general") {
  const normalized = clean(value || fallback)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!normalized) return fallback;
  if (normalized === "tithes" || normalized === "tithe") return "tithe";
  if (normalized === "offerings" || normalized === "offering") return "offering";
  if (normalized === "building" || normalized === "building_fund") return "building_fund";
  return normalized;
}

function normalizePurpose(value) {
  return clean(value).toLowerCase() === "commerce" ? "commerce" : "giving";
}

function toSearchParams(input) {
  if (input instanceof URLSearchParams) return input;
  return new URLSearchParams(input || "");
}

function firstParam(searchParams, keys) {
  for (const key of keys) {
    const value = clean(searchParams.get(key));
    if (value) return value;
  }
  return "";
}

function isActive(value = {}) {
  if (value?.archived === true) return false;
  const status = clean(value.status).toLowerCase();
  if (["archived", "inactive", "disabled", "deleted"].includes(status)) return false;
  return true;
}

function isPublicCampaign(value = {}) {
  const status = clean(value.status).toLowerCase();
  const publishStatus = clean(value.publishStatus).toLowerCase();
  const approvalStatus = clean(value.approvalStatus).toLowerCase();
  return (
    isActive(value) &&
    value?.isPublic !== false &&
    value?.visibility !== "private" &&
    (
      ["active", "live", "approved", "published"].includes(status) ||
      ["active", "live", "approved", "published"].includes(publishStatus) ||
      approvalStatus === "approved" ||
      value?.approved === true
    )
  );
}

function docToObject(snapshot, extra = {}) {
  if (!snapshot?.exists?.()) return null;
  return { id: snapshot.id, ...extra, ...(snapshot.data?.() || {}) };
}

function createFirestoreDataSource() {
  return {
    async getDoc(path) {
      const snapshot = await getDoc(doc(db, ...path.split("/").filter(Boolean)));
      return docToObject(snapshot);
    },
    async queryFirst(path, field, op, value) {
      const publicCampaignFilters = /(^|\/)campaigns$/.test(path)
        ? [
            where("status", "==", "active"),
            where("visibility", "==", "public"),
            where("approvalStatus", "==", "approved"),
            where("approved", "==", true),
          ]
        : [];
      const snapshot = await getDocs(
        query(
          collection(db, ...path.split("/").filter(Boolean)),
          where(field, op, value),
          ...publicCampaignFilters,
          ...(path === "participantsPublic" ? [where("visibility", "==", "public"), where("approved", "==", true)] : []),
          limit(1),
        )
      );
      if (snapshot.empty) return null;
      return docToObject(snapshot.docs[0]);
    },
    async queryMany(path, field, op, value, max = 25) {
      const snapshot = await getDocs(
        query(collection(db, ...path.split("/").filter(Boolean)), where(field, op, value), limit(max))
      );
      return snapshot.docs.map((item) => docToObject(item)).filter(Boolean);
    },
  };
}

// Classify a Firestore read failure by its normalized SDK CODE (never by message
// text). "miss" = the read is authoritatively empty for this caller (denied or
// absent) and must be treated as not-found; "retryable" = transient infrastructure
// or network condition; "unknown" = unexpected, surfaced as a controlled technical
// state. See docs/engineering/PUBLIC_FUNDRAISER_INCIDENT_RUN_C_CANONICAL_PREVENTION.
export function classifyReadError(error) {
  const code = String(error?.code || error?.name || "")
    .toLowerCase()
    .replace(/^firestore\//, "");
  if (code === "permission-denied" || code === "not-found" || code === "not_found") {
    return "miss";
  }
  if (
    code === "unavailable" ||
    code === "deadline-exceeded" ||
    code === "deadline_exceeded" ||
    code === "resource-exhausted" ||
    code === "cancelled" ||
    code === "aborted"
  ) {
    return "retryable";
  }
  return "unknown";
}

const ERROR_CLASS_SEVERITY = { miss: 1, retryable: 2, unknown: 3 };

// Records the worst read-error class seen during a resolution so the caller can
// distinguish "authoritatively unavailable" (miss/null) from a transient failure.
function escalate(sink, cls) {
  if (!sink || !cls) return;
  if ((ERROR_CLASS_SEVERITY[cls] || 0) > (ERROR_CLASS_SEVERITY[sink.class] || 0)) {
    sink.class = cls;
  }
}

// Fail-soft getDoc: a denial/absence yields null (a miss); the exception never
// escapes. Firestore Rules remain the sole authority on whether the read succeeds.
async function safeGetDoc(input, key, sink) {
  if (!input || typeof input.getDoc !== "function") return null;
  try {
    return await input.getDoc(key);
  } catch (error) {
    escalate(sink, classifyReadError(error));
    return null;
  }
}

// Fail-soft queryFirst with the same contract as safeGetDoc.
async function safeQueryFirst(input, path, field, op, value, sink) {
  if (!input || typeof input.queryFirst !== "function") return null;
  try {
    return await input.queryFirst(path, field, op, value);
  } catch (error) {
    escalate(sink, classifyReadError(error));
    return null;
  }
}

async function findOrganization(orgSlug, dataSource, warnings, sink) {
  const found = await readPublicOrganizationWith(async (path) => {
    const record = await safeGetDoc(dataSource, path, sink);
    if (!record) return null;
    // This transport attaches the snapshot ID. Check it before removing that
    // transport-only field and validating the closed shared wire contract.
    const {id, ...raw} = record;
    return id && id !== path.split("/").pop() ? null : raw;
  }, cleanSegment(orgSlug));
  if (found) return found;
  warnings.push("organization_not_found");
  return null;
}

async function findCampaign({ orgSlug, campaignSlug, campaignId, dataSource, warnings, sink }) {
  // Campaign reads are permitted only for approved, public, active documents;
  // queryFirst adds the same constraints because Rules are not filters.
  const wanted = cleanSegment(campaignSlug || campaignId);
  if (!orgSlug || !wanted) return null;
  const orgCampaignPath = `organizations/${orgSlug}/campaigns`;

  const direct = await safeGetDoc(dataSource, `${orgCampaignPath}/${wanted}`, sink);
  if (direct) return { ...direct, campaignSlug: direct.campaignSlug || direct.slug || direct.id || wanted };

  for (const field of ["campaignSlug", "slug"]) {
    const found = await safeQueryFirst(dataSource, orgCampaignPath, field, "==", wanted, sink);
    if (found) return { ...found, campaignSlug: found.campaignSlug || found.slug || found.id };
  }

  for (const field of ["campaignSlug", "slug", "campaignId"]) {
    const found = await safeQueryFirst(dataSource, "campaigns", field, "==", wanted, sink);
    if (found) return { ...found, campaignSlug: found.campaignSlug || found.slug || found.id };
  }

  warnings.push("campaign_not_found");
  return null;
}

function tagFundraiserSlug(found, wanted) {
  return {
    ...found,
    fundraiserSlug:
      found.fundraiserSlug || found.participantSlug || found.slug || found.id || wanted,
  };
}

function fundraiserRowMatchesOrgCampaign(found, orgSlug, campaignCandidates) {
  const rowOrg = cleanSegment(found.campaignOrgSlug || found.orgSlug || found.organizationId || found.orgId);
  const rowCampaign = cleanSegment(found.campaignSlug || found.campaignId || found.defaultCampaignId);
  return (!rowOrg || rowOrg === orgSlug) && (!rowCampaign || campaignCandidates.includes(rowCampaign));
}

// Read the sanitized public projection only. This is the sole signed-out
// resolution surface and the first authenticated surface. Never reads a private
// collection.
async function findFundraiserFromPublicProjection({ orgSlug, wanted, campaignCandidates, canonicalPath, dataSource, sink }) {
  for (const field of ["fundraiserSlug", "participantSlug", "slug"]) {
    const found = await safeQueryFirst(dataSource, "participantsPublic", field, "==", wanted, sink);
    if (found && fundraiserRowMatchesOrgCampaign(found, orgSlug, campaignCandidates)) {
      return tagFundraiserSlug(found, wanted);
    }
  }
  if (canonicalPath) {
    const byPath = await safeQueryFirst(dataSource, "participantsPublic", "canonicalPath", "==", canonicalPath, sink);
    if (byPath) return tagFundraiserSlug(byPath, wanted);
  }
  return null;
}

// Authenticated compatibility/preview fallback. Attempts the private nested
// fundraiser and legacy private collections OPTIMISTICALLY. It never evaluates
// the viewer's authorization — Firestore Rules do. Every read is fail-soft: a
// permission-denied or not-found is a miss (returns null), classified via `sink`.
async function findFundraiserFromPrivateFallback({ orgSlug, wanted, campaignCandidates, canonicalPath, dataSource, sink }) {
  for (const candidate of campaignCandidates) {
    const direct = await safeGetDoc(dataSource, `organizations/${orgSlug}/campaigns/${candidate}/fundraisers/${wanted}`, sink);
    if (direct) return tagFundraiserSlug(direct, wanted);
    const bySlug = await safeQueryFirst(
      dataSource,
      `organizations/${orgSlug}/campaigns/${candidate}/fundraisers`,
      "fundraiserSlug",
      "==",
      wanted,
      sink
    );
    if (bySlug) return tagFundraiserSlug(bySlug, wanted);
  }
  for (const collectionName of ["participants", "fundraisers"]) {
    for (const field of ["fundraiserSlug", "participantSlug", "slug"]) {
      const found = await safeQueryFirst(dataSource, collectionName, field, "==", wanted, sink);
      if (found && fundraiserRowMatchesOrgCampaign(found, orgSlug, campaignCandidates)) {
        return tagFundraiserSlug(found, wanted);
      }
    }
    if (canonicalPath) {
      const byPath = await safeQueryFirst(dataSource, collectionName, "canonicalPath", "==", canonicalPath, sink);
      if (byPath) return tagFundraiserSlug(byPath, wanted);
    }
  }
  return null;
}

async function findFundraiser({ orgSlug, campaign, campaignSlug, fundraiserSlug, dataSource, warnings, allowPrivateReads, sink }) {
  const wanted = cleanSegment(fundraiserSlug);
  if (!orgSlug || !wanted) return null;
  const campaignCandidates = [
    campaign?.id,
    campaign?.campaignSlug,
    campaign?.slug,
    campaignSlug,
  ].map(cleanSegment).filter(Boolean);
  const canonicalPath = campaignCandidates[0]
    ? `organizations/${orgSlug}/campaigns/${campaignCandidates[0]}/fundraisers/${wanted}`
    : "";

  // 1) Public projection first — the only surface a signed-out visitor may reach.
  //    A record surfaced here is public to everyone; if it is not approved (e.g. a
  //    stale/archived mirror) it must be treated as neutral, never an authorized
  //    "awaiting approval" preview. `sink.fundraiserSource` carries that provenance.
  const fromPublic = await findFundraiserFromPublicProjection({ orgSlug, wanted, campaignCandidates, canonicalPath, dataSource, sink });
  if (fromPublic) { sink.fundraiserSource = "public"; return fromPublic; }

  // 2) Private compatibility/preview fallback — authenticated visitors only.
  //    Firestore Rules decide whether these reads succeed; denials are misses.
  //    A record surfaced here passed the private read gate, so a non-approved one
  //    is a legitimate authorized preview.
  if (allowPrivateReads) {
    const fromPrivate = await findFundraiserFromPrivateFallback({ orgSlug, wanted, campaignCandidates, canonicalPath, dataSource, sink });
    if (fromPrivate) { sink.fundraiserSource = "private"; return fromPrivate; }
  }

  warnings.push("fundraiser_not_found");
  return null;
}

function donationRouteFromOrg(org, category) {
  const configured =
    org?.defaultGivingRoute ||
    org?.giving?.defaultRoute ||
    org?.givingSettings?.defaultRoute ||
    org?.publicProfile?.defaultGivingRoute ||
    org?.donationRoutes?.[category] ||
    org?.defaultFunds?.[category] ||
    org?.publicProfile?.defaultFunds?.[category] ||
    null;
  if (!configured) return null;
  if (typeof configured === "string") {
    return { ...GENERAL_GIVING_ROUTE, id: configured, slug: configured, donationRouteId: configured, isFallback: false };
  }
  return { ...GENERAL_GIVING_ROUTE, ...configured, isFallback: false };
}

async function findDonationRoute({ orgSlug, campaign, campaignSlug, explicitRouteId, category, dataSource, sink }) {
  // donationRoutes are public-safe reads; wrapped only for transient classification.
  const explicit = cleanSegment(explicitRouteId);
  const campaignKey = cleanSegment(campaign?.id || campaign?.campaignSlug || campaign?.slug || campaignSlug);
  if (orgSlug && campaignKey && explicit) {
    const route = await safeGetDoc(dataSource, `organizations/${orgSlug}/campaigns/${campaignKey}/donationRoutes/${explicit}`, sink);
    if (route) return { ...GENERAL_GIVING_ROUTE, ...route, id: route.id || explicit, slug: route.slug || explicit, isFallback: false };
  }

  const campaignDefault = cleanSegment(
    campaign?.defaultDonationRouteSlug ||
      campaign?.donationRouteSlug ||
      campaign?.givingConfig?.defaultDonationRouteSlug ||
      campaign?.publicMicrositeConfig?.defaultDonationRouteSlug
  );
  if (orgSlug && campaignKey && campaignDefault) {
    const route = await safeGetDoc(dataSource, `organizations/${orgSlug}/campaigns/${campaignKey}/donationRoutes/${campaignDefault}`, sink);
    if (route) return { ...GENERAL_GIVING_ROUTE, ...route, id: route.id || campaignDefault, slug: route.slug || campaignDefault, isFallback: false };
  }

  if (orgSlug === "the-redeemed-christian-church-of-god-chapel-of-mercy-00405731" && campaignKey === FAITH_SANCTUARY_CAMPAIGN_SLUG) {
    const route = await safeGetDoc(dataSource, `organizations/${orgSlug}/campaigns/${campaignKey}/donationRoutes/${BUILDING_FUND_DONATION_ROUTE_SLUG}`, sink);
    if (route) return { ...GENERAL_GIVING_ROUTE, ...route, id: route.id || BUILDING_FUND_DONATION_ROUTE_SLUG, slug: BUILDING_FUND_DONATION_ROUTE_SLUG, isFallback: false };
  }

  return { ...GENERAL_GIVING_ROUTE, category, givingCategory: category, platformCategory: category };
}

export function normalizePublicGivingQuery(searchParamsInput) {
  const searchParams = toSearchParams(searchParamsInput);
  const givingCategory = normalizeCategory(
    firstParam(searchParams, ["givingCategory", "category", "platformCategory"]),
    "general"
  );
  return {
    transactionPurpose: normalizePurpose(searchParams.get("transactionPurpose")),
    givingType: clean(searchParams.get("givingType")),
    givingCategory,
    category: givingCategory,
    campaignId: firstParam(searchParams, ["campaignId", "campaign"]),
    campaignSlug: clean(searchParams.get("campaignSlug")),
    routeId: clean(searchParams.get("routeId")),
    donationRouteId: firstParam(searchParams, ["donationRouteId", "routeId", "donationRouteSlug"]),
    donationRouteSlug: firstParam(searchParams, ["donationRouteSlug", "donationRouteId", "routeId"]),
    fundraiserSlug: firstParam(searchParams, ["fundraiserSlug", "participantSlug", "participantId", "slug"]),
    source: clean(searchParams.get("source") || searchParams.get("sourceContext")),
    amount: clean(searchParams.get("amount")),
    currency: clean(searchParams.get("currency")),
    interval: clean(searchParams.get("interval")),
  };
}

export function buildPublicGivingUrl({
  orgSlug,
  campaignSlug,
  campaignId,
  fundraiserSlug,
  category,
  givingCategory,
  donationRouteId,
  routeId,
  transactionPurpose = "giving",
  source,
  amount,
  currency,
  interval,
  isCustomDomain,
  hostname,
  pathOptions = {},
} = {}) {
  const org = cleanSegment(orgSlug);
  if (!org) return "/donate";
  const campaign = cleanSegment(campaignSlug || campaignId);
  const fundraiser = cleanSegment(fundraiserSlug);
  const routeOptions = {
    ...pathOptions,
    ...(typeof isCustomDomain === "boolean" ? { isCustomDomain } : {}),
    ...(hostname ? { hostname } : {}),
  };
  const params = new URLSearchParams();

  params.set("transactionPurpose", normalizePurpose(transactionPurpose));
  const normalizedCategory = normalizeCategory(givingCategory || category, "general");
  params.set("givingCategory", normalizedCategory);
  if (campaign) params.set("campaignSlug", campaign);
  if (donationRouteId || routeId) params.set("donationRouteId", cleanSegment(donationRouteId || routeId));
  if (fundraiser) params.set("fundraiserSlug", fundraiser);
  if (source) params.set("source", clean(source));
  if (amount) params.set("amount", clean(amount));
  if (currency) params.set("currency", clean(currency));
  if (interval) params.set("interval", clean(interval));

  const path = campaign && fundraiser
    ? buildPublicOrgPath(org, `/campaigns/${encodeSegment(campaign)}/f/${encodeSegment(fundraiser)}/donate`, routeOptions)
    : buildPublicOrgPath(org, "/donate", routeOptions);
  return `${path}?${params.toString()}`;
}

export async function resolvePublicGivingContext({
  orgSlug,
  campaignSlug,
  fundraiserSlug,
  searchParams,
  pathname = "",
  params = {},
  dataSource = createFirestoreDataSource(),
  // Signed-out is the safe default: private compatibility/preview reads are only
  // attempted when the caller has a settled authenticated viewer. This decides
  // WHICH reads are attempted, never what Firestore Rules permit.
  isAuthenticated = false,
} = {}) {
  const errors = [];
  const warnings = [];
  const sink = { class: null };
  const allowPrivateReads = isAuthenticated === true;
  const route = resolveCanonicalPublicRoute({ pathname, params });
  const queryValues = normalizePublicGivingQuery(searchParams);
  const resolvedOrgSlug = cleanSegment(orgSlug || params.orgSlug || route.orgSlug);
  const resolvedCampaignSlug = cleanSegment(campaignSlug || params.campaignId || queryValues.campaignSlug || queryValues.campaignId || route.campaignSlug);
  const resolvedFundraiserSlug = cleanSegment(fundraiserSlug || params.fundraiserSlug || params.slug || queryValues.fundraiserSlug || route.participantSlug);

  const buildResolution = (fundraiserResolved) => ({
    // Worst read-error class observed (null | "miss" | "retryable" | "unknown").
    errorClass: sink.class,
    fundraiserRequested: Boolean(resolvedFundraiserSlug),
    fundraiserResolved: Boolean(fundraiserResolved),
    // Which surface resolved the fundraiser: "public" (world-readable projection,
    // discoverable by anyone) or "private" (passed the authorized read gate).
    fundraiserSource: fundraiserResolved ? sink.fundraiserSource || null : null,
    attemptedPrivateReads: allowPrivateReads,
  });

  const org = await findOrganization(resolvedOrgSlug, dataSource, warnings, sink);
  if (!org) {
    errors.push("organization_not_found");
    return {
      org: null,
      orgSlug: resolvedOrgSlug,
      campaign: null,
      campaignId: null,
      campaignSlug: resolvedCampaignSlug || null,
      fundraiser: null,
      fundraiserSlug: resolvedFundraiserSlug || null,
      transactionPurpose: queryValues.transactionPurpose,
      givingCategory: queryValues.givingCategory,
      donationRouteId: null,
      donationRoute: { ...GENERAL_GIVING_ROUTE, category: queryValues.givingCategory, givingCategory: queryValues.givingCategory },
      source: "org",
      status: "notFound",
      resolution: buildResolution(false),
      errors,
      warnings,
    };
  }

  const canonicalOrgSlug = org.id || org.orgSlug || resolvedOrgSlug;
  const campaign = resolvedCampaignSlug
    ? await findCampaign({
        orgSlug: canonicalOrgSlug,
        campaignSlug: resolvedCampaignSlug,
        dataSource,
        warnings,
        sink,
      })
    : null;
  const fundraiser = resolvedFundraiserSlug
    ? await findFundraiser({
        orgSlug: canonicalOrgSlug,
        campaign,
        campaignSlug: resolvedCampaignSlug,
        fundraiserSlug: resolvedFundraiserSlug,
        dataSource,
        warnings,
        allowPrivateReads,
        sink,
      })
    : null;

  if (resolvedCampaignSlug && !campaign) errors.push("campaign_not_found");
  if (resolvedFundraiserSlug && !fundraiser) errors.push("fundraiser_not_found");

  const orgDefaultRoute = donationRouteFromOrg(org, queryValues.givingCategory);
  const campaignRoute = campaign
    ? await findDonationRoute({
        orgSlug: canonicalOrgSlug,
        campaign,
        campaignSlug: resolvedCampaignSlug,
        explicitRouteId: queryValues.donationRouteSlug,
        category: queryValues.givingCategory,
        dataSource,
        sink,
      })
    : null;

  const explicitRouteProvided = Boolean(queryValues.donationRouteId || queryValues.routeId || queryValues.donationRouteSlug);
  const donationRoute =
    explicitRouteProvided && campaignRoute
      ? campaignRoute
      : orgDefaultRoute ||
        (campaign && isPublicCampaign(campaign) ? campaignRoute : null) ||
        { ...GENERAL_GIVING_ROUTE, category: queryValues.givingCategory, givingCategory: queryValues.givingCategory, platformCategory: queryValues.givingCategory };

  const source = resolvedFundraiserSlug ? "fundraiser" : campaign ? "campaign" : "org";
  const normalized = {
    org,
    orgSlug: canonicalOrgSlug,
    campaign,
    campaignId: campaign?.id || (campaign ? resolvedCampaignSlug : null),
    campaignSlug: campaign?.campaignSlug || campaign?.slug || campaign?.id || resolvedCampaignSlug || null,
    fundraiser,
    fundraiserSlug: fundraiser?.fundraiserSlug || fundraiser?.participantSlug || fundraiser?.slug || fundraiser?.id || resolvedFundraiserSlug || null,
    transactionPurpose: queryValues.transactionPurpose,
    givingCategory: donationRoute.givingCategory || donationRoute.category || queryValues.givingCategory || "general",
    donationRouteId: donationRoute.donationRouteId || donationRoute.id || null,
    donationRoute,
    source: queryValues.source || source,
    status: errors.length ? "empty" : "ready",
    resolution: buildResolution(Boolean(fundraiser)),
    errors,
    warnings,
  };

  if (process.env.NODE_ENV !== "production") {
    console.debug("[Lightly Giving Resolver]", normalized);
  }

  return normalized;
}

export { GENERAL_GIVING_ROUTE };
